import React, { useEffect, useMemo } from "react"

import styled from "styled-components/native"

import { MediaImageFile, VideoResponse } from "@treefort/api-spec"

import { useEventEmitterValue } from "../../hooks/use-event-emitter-value"
import { useIsFocused } from "../../hooks/use-is-focused"
import useWindowDimensions from "../../hooks/use-window-dimensions"
import { IS_BOT } from "../../lib/is-bot"
import { Spacing } from "../../lib/spacing"
import { Event, VideoPlayer } from "../../lib/video-player"
import { videoPlayerManager } from "../../lib/video-player-manager"
import ActivityIndicator from "../activity-indicator"
import TokensProvider, { useTokens } from "../tokens-provider"
import { VideoPlayNextOverlay } from "../video-play-next-overlay"
import Player from "./player"
import Poster from "./poster"

// The video player is always in dark mode
const DISPLAY_MODE = "dark"

const Container = styled.View`
  position: relative;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.black};
`

/**
 * A wrapper around the VideoPlayer component that unmounts the component and
 * shows a poster instead when the player instance is suspended.
 */
function SuspendablePlayer({
  videoPlayer,
  poster,
  dimensions,
  onPressPoster,
}: {
  videoPlayer: VideoPlayer
  dimensions: { width: number; height: number }
  poster?: MediaImageFile
  onPressPoster?: () => void
}): React.JSX.Element {
  const suspended = useEventEmitterValue(
    videoPlayer,
    Event.Suspended,
    videoPlayer.getSuspended,
  )

  const focused = useIsFocused()

  // Register the video player with the AV manager. The AV manage will
  // coordinate this video player's state with any other video players as well
  // as the audio player.
  useEffect(() => {
    videoPlayerManager.registerVideoPlayer(videoPlayer)
    return () => videoPlayerManager.unregisterVideoPlayer(videoPlayer)
  }, [videoPlayer])

  // Publish our intent to start playback
  useEffect(() => {
    if (!videoPlayer.getSuspended() && videoPlayer.getTrack()) {
      videoPlayer.publishPlayIntent()
    }
  }, [videoPlayer])

  // Suspend the video player when the route is not focused (e.g. the user
  // switches to another page or tab) or the profiles overlay is open
  useEffect(() => {
    if (!focused) {
      videoPlayer.suspend()
    }
  }, [focused, videoPlayer])

  return suspended ? (
    <Poster poster={poster} onPress={onPressPoster} />
  ) : (
    <Player videoPlayer={videoPlayer} dimensions={dimensions} />
  )
}

/**
 * This takes in a VideoPlayer object, renders a video element using the
 * VideoPlayer React component (not to be confused with the object), and keeps
 * the rendered player and the player object in sync.
 */
export default function VideoPlayerInline({
  videoPlayer,
  poster,
  paddingTop,
  loading,
  onPressPoster,
  nextVideo,
}: {
  // A VideoPlayer instance. Hold on to this - you can use it to trigger actions
  // on the player or listen to state changes.
  videoPlayer?: VideoPlayer
  poster?: MediaImageFile
  paddingTop?: Spacing
  // Set this to true show a loading state when no VideoPlayer object is
  // provided. This is ignored if a VideoPlayer object is provided.
  loading?: boolean
  // Listen for press events on the poster. The poster is shown when the poster
  // props is set, no VideoPlayer object is provided, and the loading prop is
  // falsey.
  onPressPoster?: () => void
  // The video to play after the current one. If set then the play next overlay
  // will be shown when the current video finishes.
  nextVideo?: VideoResponse
}): React.JSX.Element {
  const windowDimensions = useWindowDimensions()
  const dimensions = useMemo(
    () => ({
      width: windowDimensions.width,
      // Display at a 16:9 aspect ratio but constrain the height to 2/3 of the
      // window height.
      height: Math.min(
        windowDimensions.height * (2 / 3),
        windowDimensions.width * (9 / 16),
      ),
    }),
    [windowDimensions],
  )
  const { tokens } = useTokens()
  const containerStyles = useMemo(() => {
    const paddingTopNumber =
      typeof paddingTop === "string"
        ? tokens.spacing[paddingTop]
        : paddingTop || 0
    return {
      height: dimensions.height + paddingTopNumber,
      width: dimensions.width,
      paddingTop: paddingTopNumber,
    }
  }, [paddingTop, tokens, dimensions])

  return (
    <TokensProvider displayMode={DISPLAY_MODE}>
      <Container style={containerStyles}>
        {
          // Don't load videos for bots (waste of time and bandwidth)
          IS_BOT ? (
            <Poster poster={poster} />
          ) : videoPlayer ? (
            <SuspendablePlayer
              videoPlayer={videoPlayer}
              poster={poster}
              dimensions={dimensions}
              onPressPoster={onPressPoster}
            />
          ) : loading ? (
            <ActivityIndicator size="jumbo" />
          ) : (
            <Poster poster={poster} onPress={onPressPoster} />
          )
        }
        <VideoPlayNextOverlay
          videoPlayer={videoPlayer}
          nextVideo={nextVideo}
          playerDimensions={dimensions}
        />
      </Container>
    </TokensProvider>
  )
}
